import React, { createContext, useContext } from 'react';

import { useMemoOne as useMemo } from 'use-memo-one';

import type { ProductEnvironment } from '@atlassian/forge-ui-types/environments';

import { EnvironmentContext } from '../environment';

type MetricsContextValue = {
	environment?: ProductEnvironment;
	page: string;
	product: string;
};

type MetricsContextProviderProps = {
	children: React.ReactNode;
	value: MetricsContextValue;
};

export const MetricsContext: React.Context<MetricsContextValue> =
	createContext<MetricsContextValue>({
		environment: undefined,
		product: '',
		page: '',
	});

export const MetricsContextProvider = ({
	value,
	children,
}: MetricsContextProviderProps): React.JSX.Element => {
	const envFromContext = useContext(EnvironmentContext);
	const { product, page, environment } = value;

	const memoedValue = useMemo(() => {
		return {
			environment: environment ?? envFromContext,
			product,
			page,
		};
	}, [page, environment, envFromContext, product]);

	return <MetricsContext.Provider value={memoedValue}>{children}</MetricsContext.Provider>;
};

export const useMetricsContext = (): MetricsContextValue => {
	return useContext(MetricsContext);
};
